/**
 * The window a report is measured over, as arithmetic rather than as a control.
 *
 * Split out of `RangeBar.tsx` when the bar learned to fold: a shut row has to
 * say WHICH window is in force, and working that out - is this one of the four
 * presets, and if not how does it read - is a rule worth testing rather than a
 * few ternaries inside a component. React free on purpose, so the test can call
 * it without rendering anything (AGENTS.md §4).
 *
 * `RangeBar` re-exports everything here, so no call site had to change.
 */

export interface Range {
  from: string;
  to: string;
}

export const PRESETS = ["month", "quarter", "year", "12m"] as const;
export type PresetKey = (typeof PRESETS)[number];

/** `YYYY-MM-DD` in the browser's own timezone. `toISOString` would shift to UTC
 *  and turn a summer evening into tomorrow. */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The presets, each as a window ending today. */
export function presetRange(key: PresetKey, now = new Date()): Range {
  const to = dateKey(now);
  if (key === "month") return { from: dateKey(new Date(now.getFullYear(), now.getMonth(), 1)), to };
  if (key === "quarter") {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return { from: dateKey(new Date(now.getFullYear(), q, 1)), to };
  }
  if (key === "year") return { from: dateKey(new Date(now.getFullYear(), 0, 1)), to };
  // Twelve months back to the first of that month, which is what the server
  // falls back to when it is handed no dates at all.
  return { from: dateKey(new Date(now.getFullYear(), now.getMonth() - 11, 1)), to };
}

/** Which preset this window IS, or null when it is one somebody typed. */
export function matchedPreset(range: Range, now = new Date()): PresetKey | null {
  return PRESETS.find((p) => {
    const preset = presetRange(p, now);
    return preset.from === range.from && preset.to === range.to;
  }) ?? null;
}

/**
 * One `YYYY-MM-DD` as the date fields draw it.
 *
 * The same two patterns `@wa/ui`'s DatePicker uses, and that is the point: the
 * toggle stands in for those fields while the row is shut, so the two have to
 * spell a date the same way or folding the row would appear to change it.
 *
 * An unparseable value comes back as itself rather than as "Invalid Date". The
 * field it came from is a picker, so this should not happen; if it ever does,
 * showing the raw string is the failure a reader can act on.
 */
export function formatDay(iso: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return locale === "et" ? `${d}.${mo}.${y}` : `${y}-${mo}-${d}`;
}

/**
 * What the shut toggle says the window is.
 *
 * A preset window reads as its own name, because that is what the operator
 * pressed and it is shorter than the dates behind it. Anything else reads as
 * both ends, because a hand-picked window has no name and "Custom" would tell
 * the reader nothing about which months the figures below cover.
 *
 * This is the rule that makes the fold honest. The register's folded filter row
 * carries a COUNT of what is still on; a date range is never off, so the
 * equivalent here is the window itself. A shut row over figures whose period
 * nothing on screen admits to would be the same failure one fold deeper.
 */
export function rangeLabel(
  range: Range,
  locale: string,
  presetName: (key: PresetKey) => string,
  now = new Date(),
): string {
  const preset = matchedPreset(range, now);
  if (preset) return presetName(preset);
  const from = range.from ? formatDay(range.from, locale) : "";
  const to = range.to ? formatDay(range.to, locale) : "";
  if (from && to) return `${from} - ${to}`;
  // One open end is a legal state: the pickers each carry a clear cross, and the
  // server fills the missing side in with its own default.
  return from || to;
}
